import type React from 'react'
import type { JSX } from 'react'
import type { IMultiColumnProps } from '../../MultiColumns'
import type { IMCItemWithMore } from '../MultiColumnsItem/MCItemWithMore'
import type { IMoviesDescriptions1Line } from '../MultiColumnsItem/MoviesDescriptions1Line'

import { TextBoxTypes } from 'atoms/TextBox'
import { TextBlockWithMore } from 'blocks/TextBlock/Templates'
import { MCItemDirectorsStars } from '../MultiColumnsItem/MCItemDirectorsStars'
import { MCItemWithMore } from '../MultiColumnsItem/MCItemWithMore'
import {
  DetailsAreaSC,
  MCDescriptionWithMoreTemplate,
} from './MCDescriptionWithMore.styled'

export interface IMCDescriptionWithMore extends Omit<
  IMCItemWithMore & IMoviesDescriptions1Line & IMultiColumnProps,
  'children'
> {
  children?: React.ReactNode
  descriptionTitle?: string
  /** Renders the block as the details page area that follows the banner. */
  detailsArea?: boolean
}

/**
 * The details area only has something to show when a value carries visible
 * text. WBS can answer with a value that is present but blank, and a blank
 * string still passes a truthy check, so it would reach the block as content
 * and leave the padded band wrapped around nothing.
 */
const visibleText = (value?: string): string => (value?.trim() ? value : '')

export const MCDescriptionWithMore = ({
  labels,
  text,
  directors,
  stars,
  children: _unused,
  width = 100,
  columnValue = 2,
  withUsingPortal,
  onClickMore,
  detailsArea = false,
  descriptionTitle,
  ...rest
}: IMCDescriptionWithMore): JSX.Element => {
  if (detailsArea) {
    const description = visibleText(text)
    const detailsDirectors = visibleText(directors)
    const detailsStars = visibleText(stars)

    if (!description && !detailsDirectors && !detailsStars) return <></>

    return (
      <DetailsAreaSC>
        <TextBlockWithMore
          detailsArea
          // `new/body/base` from the concept stays 16/24 at every breakpoint,
          // while Body3 steps up to 18/28 from 1640.
          font={TextBoxTypes.FontSm}
          fontDirectors={TextBoxTypes.FontSm}
          description={description}
          directors={detailsDirectors}
          stars={detailsStars}
          descriptionTitle={descriptionTitle}
          directorsTitle={rest.directorsTitle}
          starsTitle={rest.starsTitle}
          titleButton={rest.titleButton}
          maxLines={rest.maxLineMore}
          onClickMore={onClickMore}
        />
      </DetailsAreaSC>
    )
  }

  return (
    <MCDescriptionWithMoreTemplate
      columnValue={columnValue}
      width={width}
      {...rest}
    >
      <MCItemWithMore
        more={rest.more}
        episodePopup={rest.episodePopup}
        titlePopup={rest.titlePopup}
        labelsPopup={rest.labelsPopup}
        listPopup={rest.listPopup}
        titleButton={rest.titleButton}
        directorsPopup={directors}
        starsPopup={stars}
        fullInformation={rest.fullInformation}
        textDirectors={directors}
        textStars={stars}
        text={text}
        width={width}
        labels={labels}
        popupWidth={rest.popupWidth}
        withUsingPortal={withUsingPortal}
        directors={directors}
        stars={stars}
        maxLineMore={rest.maxLineMore}
        maxLineStars={rest.maxLineStars}
        maxLineDirectors={rest.maxLineDirectors}
        onClickMore={onClickMore}
        directorsTitle={rest.directorsTitle}
        starsTitle={rest.starsTitle}
      />
      {text && (
        <MCItemDirectorsStars
          directors={directors}
          width={width}
          stars={stars}
          directorsTitle={rest.directorsTitle}
          starsTitle={rest.starsTitle}
        />
      )}
    </MCDescriptionWithMoreTemplate>
  )
}
